import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import {
  ApplicationInsights,
  ITelemetryItem,
} from '@microsoft/applicationinsights-web';

import {
  CookiesState,
  useContextStore,
  useCookiesStore,
} from '../../stores/storeRoot';
import cookiesHelper from '../cookies/cookiesHelper';
import { API_CONFIG } from '../apiConfig';

class AppInsightsManager {
  public _reactPlugin = new ReactPlugin();
  private _appInsights: ApplicationInsights | null | undefined;
  private _connectionString: string;
  //Invoked for each telemetry item
  private _telemetryInitializer = (item: ITelemetryItem) => {
    const props = useContextStore.getState().appInsightsContextData ?? {};
    item.data = item.data || {};
    item.data = { ...item.data, ...props };
  };

  constructor(connectionString: string) {
    this._connectionString = connectionString;
    useCookiesStore.subscribe(this.cookieStoreCallback.bind(this));
  }

  private cookieStoreCallback(state: CookiesState, prevState: CookiesState) {
    if (state.statisticsCookieAccepted !== prevState.statisticsCookieAccepted) {
      if (state.statisticsCookieAccepted) {
        this.tryEnableTracking();
      } else {
        this.disableTracking();
      }
    }
  }

  private initializeAppInsightsObject(connectionString: string) {
    this._appInsights = new ApplicationInsights({
      config: {
        //enableAutoRouteTracking: true,
        connectionString: connectionString,
        // *** If you're adding the Click Analytics plug-in, delete the next line. ***
        //enableAutoRouteTracking: true,
        //autoTrackPageVisitTime: true,
        extensions: [this._reactPlugin],
        //cookieDomain: 'app-cropconnect-dev-weu-001.azurewebsites.net', //API_CONFIG.BASE_URL,
        // *** Add the Click Analytics plug-in. ***
        // extensions: [reactPlugin, clickPluginInstance],
        extensionConfig: {
          //*** Use only if router does not change the url ***
          //[this._reactPlugin.identifier]: { history: window.history },
          // *** Add the Click Analytics plug-in. ***
          // [clickPluginInstance.identifier]: clickPluginConfig
        },
      },
    });
    this._appInsights.addTelemetryInitializer(
      this._telemetryInitializer.bind(this)
    );
  }

  /**
   * Enables App Insights tracking. App Insights enabled with this call but additional bp context info might take a couple of ms to load
   */
  tryEnableTracking(): void {
    if (useContextStore.getState().nordzuckerInternalUser) {
      //Tracking is disabled for internal users
      this.disableTracking();
      return;
    }
    if (cookiesHelper.getStatisticsCookiesAllowed()) {
      this.initializeAppInsightsObject(this._connectionString);
      this._appInsights?.loadAppInsights();
    } else {
      this.disableTracking();
    }
  }

  /**
   * Disables App Insights tracking.
   */
  disableTracking() {
    this.ensureNoAppInsightsCookies();
    this._appInsights?.unload();
    this._appInsights = null;
  }

  ensureNoAppInsightsCookies = () => {
    cookiesHelper.setCookieValue('ai_user', null, -1);
    cookiesHelper.setCookieValue('ai_session', null, -1);
  };

  /**
   * Tracks error (if App Insights enabled)
   */
  trackError(error: Error) {
    this._reactPlugin?.trackException({ exception: error });
  }
}

export const appInsightsManager = new AppInsightsManager(
  API_CONFIG.APP_INSIGHTS_INSTRUMENTATION_KEY ?? ''
);
